/* ==========================================================================
   Cart Suggestions: Cosmo 9.x custom component
   - Fetches Shopify product recommendations (or hand-picked products) for
     what is in the cart, rendered server-side by sections/cart-suggestions.liquid
   - Hides products already in the cart, refreshes on every cart change
   - One-tap add to cart that refreshes the Cosmo drawer in place
   ========================================================================== */
(() => {
  if (customElements.get("cart-suggestions")) return;

  const sectionCache = new Map();

  const rootUrl = (config) =>
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || config.rootUrl || "/";

  const isFullCart = (data) =>
    data && typeof data.item_count === "number" && Array.isArray(data.items) && typeof data.total_price === "number";

  /** Fetch a section render and return its cards as [{ id, html }] (cached per URL). */
  const fetchCards = (url) => {
    if (!sectionCache.has(url)) {
      const request = fetch(url, { credentials: "same-origin" })
        .then((res) => (res.ok ? res.text() : ""))
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, "text/html");
          return Array.from(doc.querySelectorAll("[data-csg-card]")).map((el) => ({
            id: Number(el.dataset.productId),
            html: el.outerHTML,
          }));
        })
        .catch(() => {
          sectionCache.delete(url);
          return [];
        });
      sectionCache.set(url, request);
    }
    return sectionCache.get(url);
  };

  /** Round-robin merge so each cart item contributes its best matches first. */
  const interleave = (lists) => {
    const out = [];
    const max = Math.max(0, ...lists.map((l) => l.length));
    for (let i = 0; i < max; i++) lists.forEach((l) => l[i] && out.push(l[i]));
    return out;
  };

  class CartSuggestions extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector("[data-csg-config]");
      if (!configEl) return;
      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.warn("[cart-suggestions] Invalid config", e);
        return;
      }

      this.track = this.querySelector("[data-csg-track]");
      this.arrows = this.querySelector("[data-csg-arrows]");
      this.prevBtn = this.querySelector("[data-csg-prev]");
      this.nextBtn = this.querySelector("[data-csg-next]");
      this.drawer = this.closest("drawer-component");
      this.currentIds = "";
      this.token = 0;
      this.cart = {
        item_count: this.config.initial.item_count,
        items: (this.config.initial.product_ids || []).map((id) => ({ product_id: id })),
      };

      // Interactions (delegated so re-rendered cards keep working)
      this.addEventListener("click", (event) => {
        const add = event.target.closest("[data-csg-add]");
        if (add && this.contains(add)) {
          event.preventDefault();
          this.addToCart(add);
        }
      });
      this.addEventListener("change", (event) => {
        if (event.target.matches("[data-csg-variant]")) this.onVariantChange(event.target);
      });

      if (this.prevBtn) this.prevBtn.addEventListener("click", () => this.scrollByCard(-1));
      if (this.nextBtn) this.nextBtn.addEventListener("click", () => this.scrollByCard(1));
      this.track.addEventListener("scroll", () => this.updateArrows(), { passive: true });
      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(() => this.updateArrows());
        this.resizeObserver.observe(this.track);
      }

      // Cart changes
      this.onThemeCartUpdate = (state) => {
        if (isFullCart(state)) this.setCart(state);
        else this.fetchCart();
      };
      if (window.FoxThemeEvents && typeof window.FoxThemeEvents.subscribe === "function") {
        this.unsubscribe = window.FoxThemeEvents.subscribe("cart-update", this.onThemeCartUpdate);
      }
      this.onDocCartEvent = (event) => {
        const cart = event.detail && event.detail.cart;
        if (isFullCart(cart)) this.setCart(cart);
        else this.fetchCart();
      };
      ["cart:updated", "cart:refresh", "cart:change"].forEach((name) =>
        document.addEventListener(name, this.onDocCartEvent)
      );

      // Re-check when the drawer opens (covers carts changed by other apps)
      if (this.drawer) {
        this.drawerObserver = new MutationObserver(() => {
          if (this.drawer.hasAttribute("open")) {
            this.fetchCart();
            requestAnimationFrame(() => this.updateArrows());
          }
        });
        this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ["open"] });
      }

      // Prefetch quietly after the page settles so the drawer opens ready
      const kickoff = () => this.refresh();
      if ("requestIdleCallback" in window) requestIdleCallback(kickoff, { timeout: 2500 });
      else setTimeout(kickoff, 1200);
    }

    disconnectedCallback() {
      if (typeof this.unsubscribe === "function") this.unsubscribe();
      ["cart:updated", "cart:refresh", "cart:change"].forEach((name) =>
        document.removeEventListener(name, this.onDocCartEvent)
      );
      if (this.drawerObserver) this.drawerObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    /* ---------- Cart state ---------- */
    fetchCart() {
      clearTimeout(this.cartTimer);
      this.cartTimer = setTimeout(async () => {
        try {
          const res = await fetch(`${rootUrl(this.config)}cart.js`, {
            credentials: "same-origin",
            headers: { Accept: "application/json" },
          });
          if (res.ok) this.setCart(await res.json());
        } catch (e) {
          /* ignore — next cart event retries */
        }
      }, 150);
    }

    setCart(cart) {
      this.cart = cart;
      clearTimeout(this.refreshTimer);
      // Keep an "Added" confirmation on screen for a moment before re-sorting
      const wait = Math.max(60, (this.holdUntil || 0) - Date.now());
      this.refreshTimer = setTimeout(() => this.refresh(), wait);
    }

    /* ---------- Data ---------- */
    recommendationsUrl(productId, intent) {
      const base = this.config.recommendationsUrl || `${rootUrl(this.config)}recommendations/products`;
      return `${base}?section_id=cart-suggestions&product_id=${productId}&limit=10&intent=${intent}`;
    }

    manualUrl() {
      return `${rootUrl(this.config)}?section_id=cart-suggestions`;
    }

    async refresh() {
      const c = this.config;
      const cart = this.cart || { item_count: 0, items: [] };
      const token = ++this.token;
      const inCart = new Set(cart.items.map((item) => Number(item.product_id)));
      let cards = [];

      try {
        if (!cart.item_count) {
          if (c.showWhenEmpty && c.hasManual) cards = await fetchCards(this.manualUrl());
        } else {
          if (c.source !== "manual") {
            const seeds = [...new Set(cart.items.map((item) => Number(item.product_id)))].slice(
              0,
              c.basedOn === "latest" ? 1 : 3
            );
            const intent = c.source === "complementary" ? "complementary" : "related";
            let lists = await Promise.all(seeds.map((id) => fetchCards(this.recommendationsUrl(id, intent))));
            if (intent === "complementary" && lists.every((l) => !l.length)) {
              lists = await Promise.all(seeds.map((id) => fetchCards(this.recommendationsUrl(id, "related"))));
            }
            cards = interleave(lists);
          }
          const usable = new Set(cards.filter((card) => !inCart.has(card.id)).map((card) => card.id));
          const needManual = c.source === "manual" || (c.fillManual && usable.size < c.limit);
          if (needManual && c.hasManual) cards = cards.concat(await fetchCards(this.manualUrl()));
        }
      } catch (e) {
        cards = [];
      }

      if (token !== this.token) return; // a newer refresh superseded this one

      const seen = new Set();
      const final = cards
        .filter((card) => {
          if (!card.id || inCart.has(card.id) || seen.has(card.id)) return false;
          seen.add(card.id);
          return true;
        })
        .slice(0, Math.max(1, Number(c.limit) || 6));

      this.render(final);
    }

    /* ---------- Rendering ---------- */
    render(cards) {
      const ids = cards.map((card) => card.id).join(",");
      if (ids === this.currentIds) return;

      const previous = this.currentIds ? this.currentIds.split(",").map(Number) : [];
      const nextIds = new Set(cards.map((card) => card.id));
      const leaving = previous.filter((id) => !nextIds.has(id));
      this.currentIds = ids;

      const paint = () => {
        if (!cards.length) {
          this.hidden = true;
          this.track.innerHTML = "";
          return;
        }
        const wasHidden = this.hidden;
        const prevSet = new Set(previous);
        this.track.innerHTML = cards.map((card) => card.html).join("");
        this.track.querySelectorAll("[data-csg-card]").forEach((el) => {
          el.setAttribute("role", "listitem");
          if (!wasHidden && !prevSet.has(Number(el.dataset.productId))) el.classList.add("is-entering");
        });
        this.hidden = false;
        if (wasHidden) {
          this.track.scrollLeft = 0;
          this.track.classList.remove("is-refreshing");
          void this.track.offsetWidth;
          this.track.classList.add("is-refreshing");
        }
        requestAnimationFrame(() => this.updateArrows());
      };

      // Let removed cards (e.g. just added to the cart) fade out first
      const leavingEls = leaving
        .map((id) => this.track.querySelector(`[data-csg-card][data-product-id="${id}"]`))
        .filter(Boolean);
      if (leavingEls.length && !this.hidden) {
        leavingEls.forEach((el) => el.classList.add("is-leaving"));
        setTimeout(paint, 280);
      } else {
        paint();
      }
    }

    /* ---------- Carousel ---------- */
    scrollByCard(direction) {
      const card = this.track.querySelector("[data-csg-card]");
      if (!card) return;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 8;
      const rtl = getComputedStyle(this).direction === "rtl";
      this.track.scrollBy({ left: (card.offsetWidth + gap) * direction * (rtl ? -1 : 1), behavior: "smooth" });
    }

    updateArrows() {
      if (!this.arrows) return;
      const { scrollWidth, clientWidth } = this.track;
      const overflow = scrollWidth - clientWidth > 4;
      this.arrows.hidden = !overflow;
      if (!overflow) return;
      const pos = Math.abs(this.track.scrollLeft);
      this.prevBtn.disabled = pos <= 2;
      this.nextBtn.disabled = pos >= scrollWidth - clientWidth - 2;
    }

    /* ---------- Variants ---------- */
    onVariantChange(select) {
      const card = select.closest("[data-csg-card]");
      const dataEl = card && card.querySelector("[data-csg-variants]");
      if (!dataEl) return;
      let variants = [];
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }
      const variant = variants.find((v) => String(v.id) === select.value);
      if (!variant) return;

      const price = card.querySelector("[data-csg-price]");
      if (price) {
        price.innerHTML =
          `<span class="csg-card__price-now">${variant.price}</span>` +
          (variant.compare ? `<s class="csg-card__price-was">${variant.compare}</s>` : "");
      }
      card.classList.toggle("is-on-sale", Boolean(variant.compare));

      const img = card.querySelector("[data-csg-image]");
      if (img && variant.image) {
        img.src = variant.image;
        if (variant.srcset) img.srcset = variant.srcset;
      }
      card.querySelectorAll('a[href*="/products/"]').forEach((a) => {
        if (variant.url) a.href = variant.url;
      });

      const button = card.querySelector("[data-csg-add]");
      if (button) {
        button.dataset.variantId = variant.id;
        button.disabled = !variant.available;
      }
      this.showError(card, "");
    }

    /* ---------- Add to cart ---------- */
    async addToCart(button) {
      if (button.classList.contains("is-loading") || button.disabled) return;
      const card = button.closest("[data-csg-card]");
      const variantId = button.dataset.variantId;
      if (!variantId) return;

      this.showError(card, "");
      button.classList.add("is-loading");
      button.setAttribute("aria-busy", "true");

      const cartDrawer = document.querySelector("cart-drawer");
      const formData = new FormData();
      formData.append("id", variantId);
      formData.append("quantity", 1);
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === "function") {
        formData.append("sections", cartDrawer.getSectionsToRender().map((s) => s.id));
        formData.append("sections_url", window.location.pathname);
      }

      const addUrl =
        (window.FoxThemeSettings && window.FoxThemeSettings.routes && window.FoxThemeSettings.routes.cart_add_url) ||
        this.config.cartAddUrl ||
        "/cart/add";

      try {
        const res = await fetch(addUrl, {
          method: "POST",
          credentials: "same-origin",
          headers: { Accept: "application/javascript", "X-Requested-With": "XMLHttpRequest" },
          body: formData,
        });
        const data = await res.json();

        if (!res.ok || data.status) {
          this.showError(card, data.description || data.message || "Sorry, this item couldn't be added.");
          button.classList.remove("is-loading");
          button.removeAttribute("aria-busy");
          return;
        }

        button.classList.remove("is-loading");
        button.classList.add("is-done");
        button.removeAttribute("aria-busy");
        this.holdUntil = Date.now() + 900;

        // Let Cosmo re-render the drawer items, totals and cart count
        if (window.FoxThemeEvents && typeof window.FoxThemeEvents.emit === "function" && data.sections) {
          window.FoxThemeEvents.emit("cart-update", { ...data, source: "cart-suggestions" });
        } else {
          document.dispatchEvent(new CustomEvent("cart:refresh", { detail: { open: true } }));
        }
        document.dispatchEvent(new CustomEvent("cart:updated", { detail: { source: "cart-suggestions" } }));

        // The card then drops out of the list (it's in the cart now)
        this.fetchCart();
      } catch (e) {
        console.error("[cart-suggestions]", e);
        this.showError(card, "Something went wrong. Please try again.");
        button.classList.remove("is-loading");
        button.removeAttribute("aria-busy");
      }
    }

    showError(card, message) {
      const el = card && card.querySelector("[data-csg-error]");
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }
  }

  customElements.define("cart-suggestions", CartSuggestions);
})();
